import * as RadixTooltip from "@radix-ui/react-tooltip";
import type { TooltipProps } from "@radix-ui/react-tooltip";
import type React from "react";
import {
  type KeyboardEventHandler,
  type MouseEventHandler,
  useEffect,
  useRef,
  useState,
} from "react";
import { useThrottleClick } from "../../../hooks/useThrottleClick";
import Tooltip from "./Tooltip";

export interface TooltipProviderProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onClick"> {
  children: React.ReactNode;
  critical?: boolean;
  throttle?: number;
  noTooltip?: boolean;
  suppressTooltip?: boolean; // renders the containers children without the tooltip
  disabled?: boolean;
  open?: boolean;
  closeOnClick?: boolean;
  primaryContent?: string | React.JSX.Element;
  delay?: number;
  onClick?: MouseEventHandler<HTMLDivElement> | "openTooltip";
}
const TooltipProvider = ({
  children,
  critical,
  noTooltip,
  suppressTooltip,
  disabled,
  primaryContent,
  onClick,
  className,
  open: externalOpen = false,
  closeOnClick = true,
  delay = 700,
  throttle = 200,
}: TooltipProviderProps) => {
  const [triggerTooltipShake, setTriggerTooltipShake] = useState(false);
  const [open, setOpen] = useState(externalOpen);
  const [hovered, setHovered] = useState(false);
  const mouseLeaveTimeout = useRef<ReturnType<typeof setTimeout>>();
  const mouseEnterTimeout = useRef<ReturnType<typeof setTimeout>>();
  const autoCloseTimeout = useRef<ReturnType<typeof setTimeout>>();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setOpen(externalOpen);
  }, [externalOpen]);

  const throttleClick = useThrottleClick(
    typeof onClick === "function" ? onClick : undefined,
    throttle,
  );

  const handleTap = (event) => {
    if (!("ontouchstart" in window || navigator.maxTouchPoints > 0)) return;
    if (disabled || typeof onClick === "function") event.stopPropagation();
    setOpen(!!primaryContent);
    if (disabled) setTriggerTooltipShake(!triggerTooltipShake);
    if (!disabled && typeof onClick === "function") throttleClick(event);
    return true;
  };

  const handleClick: MouseEventHandler<HTMLDivElement> = (event) => {
    clearTimeout(mouseEnterTimeout.current);
    if (handleTap(event)) return;
    if (disabled || onClick === "openTooltip") {
      if (!open) setOpen(!!primaryContent);
      if (disabled) {
        setTriggerTooltipShake(!triggerTooltipShake);
      }
      clearTimeout(autoCloseTimeout.current);
      autoCloseTimeout.current = setTimeout(() => {
        if (!hovered) setOpen(false);
      }, 1500);

      event.stopPropagation();
      return;
    }
    if (closeOnClick) setOpen(false);

    if (typeof onClick === "function") throttleClick(event);
  };

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      if (disabled) {
        event.preventDefault();
        handleClick(event as any);
      }
    }
  };

  if (noTooltip)
    return (
      <div
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={className}
      >
        {children}
      </div>
    );

  const onMouseEnter = () => {
    setHovered(true);
    clearTimeout(mouseLeaveTimeout.current);
    if (disabled) return;
    mouseEnterTimeout.current = setTimeout(() => {
      setOpen(!!primaryContent);
    }, delay);
  };

  const onMouseLeave = () => {
    setHovered(false);
    clearTimeout(mouseEnterTimeout.current);
    mouseLeaveTimeout.current = setTimeout(() => {
      setOpen(false);
    }, delay);
  };

  const modalStack = document.getElementById("modal-stack");
  return (
    <RadixTooltip.Provider aria-label="tooltip">
      <Tooltip
        style={{ zIndex: 99 }}
        hasArrow
        critical={critical}
        primaryContent={primaryContent}
        shake={triggerTooltipShake}
        onPointerDownOutside={() => {
          if (!hovered) setOpen(false);
        }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        open={suppressTooltip ? false : open}
        portalContainer={
          modalStack?.contains(ref.current) ? modalStack : undefined
        }
      >
        <div
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          className={className}
          ref={ref}
        >
          {children}
        </div>
      </Tooltip>
    </RadixTooltip.Provider>
  );
};

export default TooltipProvider;
